import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetchAuthSession } from 'aws-amplify/auth';
import { axiosInstance } from '../../axiosClient';
import { FEDERATED_REQUEST_FLAG, readFederatedSession } from '../../federated-session';
import { IMPERSONATED_REQUEST_FLAG, readImpersonationSession } from '../../impersonation';
import { msUntilJwtExpiry } from './anonymous-session/decode-jwt-exp';

/** Refresh the cached token this long before it actually expires. */
const TOKEN_REFRESH_LEAD_MS = 60 * 1000;
/** Fallback cadence for opaque/anonymous tokens that carry no `exp` claim. */
const TOKEN_REFETCH_FALLBACK_MS = 5 * 60 * 1000;
/** Floor on the refetch interval so a near-expiry/unrefreshable token can't busy-loop fetchAuthSession. */
const TOKEN_REFETCH_MIN_MS = 30 * 1000;

let interceptorReference: number | null = null;

type ResolvedToken = { token: string | null; impersonated: boolean; federated: boolean };

async function resolveEffectiveToken(anonymousToken?: string | null): Promise<ResolvedToken> {
  // A Korint-staff impersonation session wins over the operator's own Amplify
  // session, so a staff member who is also signed in still acts as the target user.
  const impersonation = readImpersonationSession();
  if (impersonation) {
    return { token: impersonation.token, impersonated: true, federated: false };
  }

  const federated = readFederatedSession();
  if (federated) {
    return { token: federated.token, impersonated: false, federated: true };
  }

  let cognitoToken: string | undefined;
  try {
    // fetchAuthSession transparently refreshes an expired access token using the
    // refresh token, so resolving it per request keeps long-lived sessions authenticated.
    const response = await fetchAuthSession();
    cognitoToken = response.tokens?.accessToken?.toString();
  } catch {
    // Unauthenticated users will throw — fall through to anonymous token
  }

  return { token: cognitoToken ?? anonymousToken ?? null, impersonated: false, federated: false };
}

export function clearBearerTokenInterceptor() {
  if (interceptorReference !== null) {
    axiosInstance.interceptors.request.eject(interceptorReference);
    interceptorReference = null;
  }
}

export function useAddBearerTokenInAxios(anonymousToken?: string | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Resolve a fresh token on every request rather than capturing one in the
    // interceptor closure — that stale capture was the cause of 401s on long sessions.
    clearBearerTokenInterceptor();
    interceptorReference = axiosInstance.interceptors.request.use(async axiosConfig => {
      const { token, impersonated, federated } = await resolveEffectiveToken(anonymousToken);
      queryClient.setQueryData(['token', anonymousToken], token);
      if (token) {
        axiosConfig.headers.Authorization = `Bearer ${token}`;
      }
      if (impersonated) {
        (axiosConfig as typeof axiosConfig & Record<string, unknown>)[IMPERSONATED_REQUEST_FLAG] =
          true;
      }
      if (federated) {
        (axiosConfig as typeof axiosConfig & Record<string, unknown>)[FEDERATED_REQUEST_FLAG] =
          true;
      }
      return axiosConfig;
    });

    return () => clearBearerTokenInterceptor();
  }, [anonymousToken, queryClient]);

  // The interceptor above keeps axios requests fresh and the cache hot while the app
  // is active. This query owns the two cases it can't: exposing the token as the hook's
  // return value (gating in auth-required.tsx) and refreshing the cache during idle
  // periods for non-axios consumers (the conversation stream reads it via getQueryData).
  // The poll fires just before the token's own expiry rather than on a fixed cadence,
  // so it adapts automatically if the Cognito TTL changes.
  const { data: token } = useQuery({
    queryKey: ['token', anonymousToken],
    queryFn: async () => (await resolveEffectiveToken(anonymousToken)).token,
    refetchInterval: query => {
      const msToExpiry = query.state.data ? msUntilJwtExpiry(query.state.data) : null;
      if (msToExpiry === null) return TOKEN_REFETCH_FALLBACK_MS;
      return Math.max(msToExpiry - TOKEN_REFRESH_LEAD_MS, TOKEN_REFETCH_MIN_MS);
    },
    refetchIntervalInBackground: true,
  });

  return token ?? null;
}
